import {
  ArgumentNode,
  DocumentNode,
  FieldNode,
  IntrospectionField,
  parse,
  SelectionNode,
  TypeKind,
  VariableDefinitionNode,
  Kind
} from "graphql";
import { QUERY_TYPES } from "ra-data-graphql";
import { DELETE, GET_LIST, GET_MANY, GET_MANY_REFERENCE } from "react-admin";
import { IntrospectionResult, Resource } from "./constants/interfaces";
import {
  BlackListFragment,
  FetchType,
  isDocumentNodeFragment,
  ResourceFragment,
  WhiteListFragment
} from "./types";
import upperFirst from "lodash/upperFirst";
import getFinalType from "./utils/getFinalType";
import * as gqlTypes from "./utils/gqlTypes";
import isList from "./utils/isList";
import isRequired from "./utils/isRequired";

export interface Query {
  name?: string;
  args: IntrospectionField[];
}

const FIELD_EXCLUSIONS = new Set([
  "Location:Availability:availability",
  "Location:Appointment:appointments",
  "Location:Order:orders",

  "User:Availability:availability",
  "User:Appointment:appointments",
  "User:AppointmentService:appointment_services",
  "User:Appointment:booked_appointments",
  "User:Order:Order",
  "User:Transaction:Transaction",

  "Service:TxService:tx_services",
  "Service:TxAddon:tx_addons",
  "Service:AppointmentService:appointment_services",
  // "Service:ServiceAddOn:add_ons",
  // I can not remove "core_services" right now because it is used in addOn (service) edit
  // "Service:ServiceAddOn:core_services",

  "Artist:ArtistAvailability:availabilities",
  "Artist:AppointmentService:AppointmentService",
  "Artist:TxService:txServices",
  "Artist:TxAddon:txAddons"
]);

const shouldExcludeField = (
  resourceName: string,
  typeName: string,
  fieldName: string
): boolean => {
  const key = `${resourceName}:${typeName}:${fieldName}`;
  return FIELD_EXCLUSIONS.has(key);
};

export const buildFields =
  (introspectionResults: IntrospectionResult, resourceName?: string) =>
  (
    fields: IntrospectionField[],
    maxNestingDepth = 1,
    depth = 0
  ): FieldNode[] => {
    return fields.reduce((acc: FieldNode[], field) => {
      const type = getFinalType(field.type);

      if (type.name.startsWith("_")) {
        return acc;
      }
      // skip if the field has mandatory args
      if (field.args.some((arg) => arg.type.kind === "NON_NULL")) {
        return acc;
      }

      if (type.kind !== TypeKind.OBJECT) {
        return [...acc, gqlTypes.field(gqlTypes.name(field.name))];
      }

      const linkedResource = introspectionResults.resources.find(
        (r) => r.type.name === type.name
      );

      if (
        resourceName &&
        shouldExcludeField(resourceName, type.name, field.name)
      ) {
        return acc;
      }

      if (linkedResource) {
        const selectionSet = [gqlTypes.field(gqlTypes.name("id"))];
        if (linkedResource.type.name === "ReminderSetting") {
          selectionSet.push(
            gqlTypes.field(gqlTypes.name("type")),
            gqlTypes.field(gqlTypes.name("value"))
          );
        }
        if (linkedResource.type.name === "ArtistAvailability") {
          selectionSet.push(
            gqlTypes.field(gqlTypes.name("artist"), {
              selectionSet: gqlTypes.selectionSet([
                gqlTypes.field(gqlTypes.name("id")),
                gqlTypes.field(gqlTypes.name("user"), {
                  selectionSet: gqlTypes.selectionSet([
                    gqlTypes.field(gqlTypes.name("id")),
                    gqlTypes.field(gqlTypes.name("first_name")),
                    gqlTypes.field(gqlTypes.name("last_name"))
                  ])
                })
              ])
            })
          );
        }
        if (linkedResource.type.name === "Role") {
          selectionSet.push(gqlTypes.field(gqlTypes.name("role")));
        }
        if (linkedResource.type.name === "MenuSection") {
          selectionSet.push(
            gqlTypes.field(gqlTypes.name("name")),
            gqlTypes.field(gqlTypes.name("section_position")),
            gqlTypes.field(gqlTypes.name("menu_section_services"), {
              selectionSet: gqlTypes.selectionSet([
                gqlTypes.field(gqlTypes.name("id")),
                gqlTypes.field(gqlTypes.name("section_position")),
                gqlTypes.field(gqlTypes.name("service"), {
                  selectionSet: gqlTypes.selectionSet([
                    gqlTypes.field(gqlTypes.name("id")),
                    gqlTypes.field(gqlTypes.name("name")),
                    gqlTypes.field(gqlTypes.name("price")),
                    gqlTypes.field(gqlTypes.name("duration"))
                  ])
                })
              ])
            })
          );
        }
        if (linkedResource.type.name === "AppointmentService") {
          selectionSet.push(
            gqlTypes.field(gqlTypes.name("id")),
            gqlTypes.field(gqlTypes.name("start_time")),
            gqlTypes.field(gqlTypes.name("end_time")),
            gqlTypes.field(gqlTypes.name("person_number")),
            gqlTypes.field(gqlTypes.name("person_name")),
            gqlTypes.field(gqlTypes.name("order")),
            gqlTypes.field(gqlTypes.name("artist"), {
              selectionSet: gqlTypes.selectionSet([
                gqlTypes.field(gqlTypes.name("id")),
                gqlTypes.field(gqlTypes.name("cover_photo_url")),
                gqlTypes.field(gqlTypes.name("user"), {
                  selectionSet: gqlTypes.selectionSet([
                    gqlTypes.field(gqlTypes.name("id")),
                    gqlTypes.field(gqlTypes.name("first_name")),
                    gqlTypes.field(gqlTypes.name("last_name")),
                    gqlTypes.field(gqlTypes.name("photo_url"))
                  ])
                })
              ])
            }),
            gqlTypes.field(gqlTypes.name("service"), {
              selectionSet: gqlTypes.selectionSet([
                gqlTypes.field(gqlTypes.name("id")),
                gqlTypes.field(gqlTypes.name("name")),
                gqlTypes.field(gqlTypes.name("duration")),
                gqlTypes.field(gqlTypes.name("price"))
              ])
            }),
            gqlTypes.field(gqlTypes.name("add_ons"), {
              selectionSet: gqlTypes.selectionSet([
                gqlTypes.field(gqlTypes.name("id")),
                gqlTypes.field(gqlTypes.name("service_id")),
                gqlTypes.field(gqlTypes.name("add_on_service_id")),
                gqlTypes.field(gqlTypes.name("core_service"), {
                  selectionSet: gqlTypes.selectionSet([
                    gqlTypes.field(gqlTypes.name("id")),
                    gqlTypes.field(gqlTypes.name("name")),
                    gqlTypes.field(gqlTypes.name("price")),
                    gqlTypes.field(gqlTypes.name("duration")),
                    gqlTypes.field(gqlTypes.name("category"))
                  ])
                })
              ])
            })
          );
        }
        if (linkedResource.type.name === "Order") {
          selectionSet.push(
            gqlTypes.field(gqlTypes.name("id")),
            gqlTypes.field(gqlTypes.name("payment_status")),
            gqlTypes.field(gqlTypes.name("amount_remaining")),
            gqlTypes.field(gqlTypes.name("full_amount")),
            gqlTypes.field(gqlTypes.name("tip_amount")),
            gqlTypes.field(gqlTypes.name("full_amount")),
            gqlTypes.field(gqlTypes.name("transactions"), {
              selectionSet: gqlTypes.selectionSet([
                gqlTypes.field(gqlTypes.name("id")),
                gqlTypes.field(gqlTypes.name("status")),
                gqlTypes.field(gqlTypes.name("amount")),
                gqlTypes.field(gqlTypes.name("user_pays_fees")),
                gqlTypes.field(gqlTypes.name("payment_fees")),
                gqlTypes.field(gqlTypes.name("total_charged_amount")),
                gqlTypes.field(gqlTypes.name("payment_fees_percent")),
                gqlTypes.field(gqlTypes.name("platform_take_percent")),
                gqlTypes.field(gqlTypes.name("total_tip_amount"))
              ])
            }),
            gqlTypes.field(gqlTypes.name("tx_cancellation_policy"), {
              selectionSet: gqlTypes.selectionSet([
                gqlTypes.field(gqlTypes.name("id")),
                gqlTypes.field(gqlTypes.name("late_cancellation_hours"))
              ])
            })
          );
        }
        if (linkedResource.type.name === "ServiceAddOn") {
          selectionSet.push(
            gqlTypes.field(gqlTypes.name("id")),
            gqlTypes.field(gqlTypes.name("service_id")),
            gqlTypes.field(gqlTypes.name("add_on_service_id")),
            gqlTypes.field(gqlTypes.name("core_service"), {
              selectionSet: gqlTypes.selectionSet([
                gqlTypes.field(gqlTypes.name("id")),
                gqlTypes.field(gqlTypes.name("name")),
                gqlTypes.field(gqlTypes.name("price")),
                gqlTypes.field(gqlTypes.name("duration")),
                gqlTypes.field(gqlTypes.name("category"))
              ])
            })
          );
        }
        if (linkedResource.type.name === "Artist") {
          selectionSet.push(
            gqlTypes.field(gqlTypes.name("id")),
            gqlTypes.field(gqlTypes.name("user_id")),
            gqlTypes.field(gqlTypes.name("cover_photo_url")),
            gqlTypes.field(gqlTypes.name("service_exclusions"), {
              selectionSet: gqlTypes.selectionSet([
                gqlTypes.field(gqlTypes.name("id")),
                gqlTypes.field(gqlTypes.name("artist_id")),
                gqlTypes.field(gqlTypes.name("service_id"))
              ])
            }),
            gqlTypes.field(gqlTypes.name("user"), {
              selectionSet: gqlTypes.selectionSet([
                gqlTypes.field(gqlTypes.name("id")),
                gqlTypes.field(gqlTypes.name("first_name")),
                gqlTypes.field(gqlTypes.name("last_name")),
                gqlTypes.field(gqlTypes.name("photo_url")),
                gqlTypes.field(gqlTypes.name("joined_company_id"))
              ])
            })
          );
        }
        if (linkedResource.type.name === "Appointment") {
          selectionSet.push(
            gqlTypes.field(gqlTypes.name("id")),
            gqlTypes.field(gqlTypes.name("notes")),
            gqlTypes.field(gqlTypes.name("is_artist_specified")),
            gqlTypes.field(gqlTypes.name("full_amount")),
            gqlTypes.field(gqlTypes.name("start_time")),
            gqlTypes.field(gqlTypes.name("end_time")),
            gqlTypes.field(gqlTypes.name("appointment_services"), {
              selectionSet: gqlTypes.selectionSet([
                gqlTypes.field(gqlTypes.name("id")),
                gqlTypes.field(gqlTypes.name("artist"), {
                  selectionSet: gqlTypes.selectionSet([
                    gqlTypes.field(gqlTypes.name("id"))
                  ])
                })
              ])
            }),
            gqlTypes.field(gqlTypes.name("location"), {
              selectionSet: gqlTypes.selectionSet([
                gqlTypes.field(gqlTypes.name("id")),
                gqlTypes.field(gqlTypes.name("company_id")),
                gqlTypes.field(gqlTypes.name("name")),
                gqlTypes.field(gqlTypes.name("nickname")),
                gqlTypes.field(gqlTypes.name("timezone"))
              ])
            })
          );
        }
        if (linkedResource.type.name === "Location") {
          selectionSet.push(
            gqlTypes.field(gqlTypes.name("id")),
            gqlTypes.field(gqlTypes.name("company_id")),
            gqlTypes.field(gqlTypes.name("name")),
            gqlTypes.field(gqlTypes.name("nickname")),
            gqlTypes.field(gqlTypes.name("timezone")),
            gqlTypes.field(gqlTypes.name("company"), {
              selectionSet: gqlTypes.selectionSet([
                gqlTypes.field(gqlTypes.name("id")),
                gqlTypes.field(gqlTypes.name("stripe_user_id")),
                gqlTypes.field(gqlTypes.name("late_cancellation_hours")),
                gqlTypes.field(gqlTypes.name("user_pays_fees"))
              ])
            })
          );
        }
        if (linkedResource.type.name === "Service") {
          selectionSet.push(
            gqlTypes.field(gqlTypes.name("id")),
            gqlTypes.field(gqlTypes.name("name")),
            gqlTypes.field(gqlTypes.name("description")),
            gqlTypes.field(gqlTypes.name("company_id")),
            gqlTypes.field(gqlTypes.name("duration")),
            gqlTypes.field(gqlTypes.name("price")),
            gqlTypes.field(gqlTypes.name("special_conditions"))
          );
        }
        if (linkedResource.type.name === "CompanyUserDescription") {
          selectionSet.push(
            gqlTypes.field(gqlTypes.name("id")),
            gqlTypes.field(gqlTypes.name("user_id")),
            gqlTypes.field(gqlTypes.name("company_id")),
            gqlTypes.field(gqlTypes.name("description"))
          );
        }
        if (
          linkedResource.type.name === "User" &&
          resourceName !== "Availability"
        ) {
          selectionSet.push(
            gqlTypes.field(gqlTypes.name("first_name")),
            gqlTypes.field(gqlTypes.name("last_name")),
            gqlTypes.field(gqlTypes.name("photo_url")),
            gqlTypes.field(gqlTypes.name("phone_number")),
            gqlTypes.field(gqlTypes.name("libphonenumber")),
            gqlTypes.field(gqlTypes.name("email")),
            gqlTypes.field(gqlTypes.name("joined_company_id")),
            gqlTypes.field(gqlTypes.name("appointments"), {
              arguments: [
                gqlTypes.argument(gqlTypes.name("take"), {
                  kind: Kind.INT,
                  value: "2"
                })
              ],
              selectionSet: gqlTypes.selectionSet([
                gqlTypes.field(gqlTypes.name("id"))
              ])
            }),
            gqlTypes.field(gqlTypes.name("company"), {
              selectionSet: gqlTypes.selectionSet([
                gqlTypes.field(gqlTypes.name("id"))
              ])
            })
          );
        }
        if (linkedResource.type.name === "Transaction") {
          selectionSet.push(
            gqlTypes.field(gqlTypes.name("id")),
            gqlTypes.field(gqlTypes.name("total_tip_amount")),
            gqlTypes.field(gqlTypes.name("tipping_type")),
            gqlTypes.field(gqlTypes.name("amount")),
            gqlTypes.field(gqlTypes.name("user_pays_fees")),
            gqlTypes.field(gqlTypes.name("payment_fees")),
            gqlTypes.field(gqlTypes.name("total_charged_amount")),
            gqlTypes.field(gqlTypes.name("payment_fees_percent")),
            gqlTypes.field(gqlTypes.name("platform_take_percent")),
            gqlTypes.field(gqlTypes.name("status")),
            gqlTypes.field(gqlTypes.name("type")),
            gqlTypes.field(gqlTypes.name("created_at"))
          );
        }
        if (linkedResource.type.name === "ArtistServiceExclusion") {
          selectionSet.push(
            gqlTypes.field(gqlTypes.name("id")),
            gqlTypes.field(gqlTypes.name("artist_id")),
            gqlTypes.field(gqlTypes.name("service_id"))
          );
        }
        if (linkedResource.type.name === "TxAddon") {
          selectionSet.push(
            gqlTypes.field(gqlTypes.name("id")),
            gqlTypes.field(gqlTypes.name("artist_id"))
          );
        }
        if (linkedResource.type.name === "TxService") {
          selectionSet.push(
            gqlTypes.field(gqlTypes.name("id")),
            gqlTypes.field(gqlTypes.name("artist_id")),
            gqlTypes.field(gqlTypes.name("artist"), {
              selectionSet: gqlTypes.selectionSet([
                gqlTypes.field(gqlTypes.name("id")),
                gqlTypes.field(gqlTypes.name("cover_photo_url")),
                gqlTypes.field(gqlTypes.name("user"), {
                  selectionSet: gqlTypes.selectionSet([
                    gqlTypes.field(gqlTypes.name("id")),
                    gqlTypes.field(gqlTypes.name("first_name")),
                    gqlTypes.field(gqlTypes.name("last_name")),
                    gqlTypes.field(gqlTypes.name("photo_url"))
                  ])
                })
              ])
            })
          );
        }
        return [
          ...acc,
          gqlTypes.field(gqlTypes.name(field.name), {
            selectionSet: gqlTypes.selectionSet(selectionSet)
          })
        ];
      }

      const linkedType = introspectionResults.types.find(
        (t) => t.name === type.name
      );

      // linkedtypes  (= nested objects) are currently a bit problematic
      // it is handy to have them, but they be slow to fetch
      // we therefore limit the depth
      if (linkedType && depth < maxNestingDepth) {
        return [
          ...acc,
          gqlTypes.field(gqlTypes.name(field.name), {
            selectionSet: gqlTypes.selectionSet(
              buildFields(introspectionResults)(
                (linkedType as any).fields,
                maxNestingDepth,
                depth + 1
              )
            )
          })
        ];
      }

      return acc;
    }, [] as FieldNode[]);
  };

export const getArgType = (arg: IntrospectionField) => {
  const type = getFinalType(arg.type);
  const required = isRequired(arg.type);
  const list = isList(arg.type);

  if (list) {
    if (required) {
      return gqlTypes.listType(
        gqlTypes.nonNullType(gqlTypes.namedType(gqlTypes.name(type.name)))
      );
    }
    return gqlTypes.listType(gqlTypes.namedType(gqlTypes.name(type.name)));
  }

  if (required) {
    return gqlTypes.nonNullType(gqlTypes.namedType(gqlTypes.name(type.name)));
  }

  return gqlTypes.namedType(gqlTypes.name(type.name));
};

export const buildArgs = (
  query: Query,
  variables: { [key: string]: any } = {}
) => {
  if (query.args.length === 0) {
    return [];
  }

  const validVariables = Object.keys(variables).filter(
    (k) => typeof variables[k] !== "undefined"
  );
  return query.args
    .filter((arg) => validVariables.includes(arg.name))
    .reduce(
      (acc: ArgumentNode[], arg) => [
        ...acc,
        gqlTypes.argument(
          gqlTypes.name(arg.name),
          gqlTypes.variable(gqlTypes.name(arg.name))
        )
      ],
      [] as ArgumentNode[]
    );
};

export const buildApolloArgs = (
  query: Query,
  variables: { [key: string]: any } = {}
) => {
  if (query.args.length === 0) {
    return [];
  }

  const validVariables = Object.keys(variables).filter(
    (k) => typeof variables[k] !== "undefined"
  );

  return query.args
    .filter((arg) => validVariables.includes(arg.name))
    .reduce(
      (acc: VariableDefinitionNode[], arg) => [
        ...acc,
        gqlTypes.variableDefinition(
          gqlTypes.variable(gqlTypes.name(arg.name)),
          getArgType(arg)
        )
      ],
      [] as VariableDefinitionNode[]
    );
};

//TODO: validate fragment against the schema
const buildFieldsFromFragment = (
  fragment: DocumentNode | string,
  resourceName: string,
  fetchType: string
): SelectionNode[] => {
  let parsedFragment = {};

  if (
    typeof fragment === "object" &&
    fragment.kind &&
    fragment.kind === "Document"
  ) {
    parsedFragment = fragment;
  }

  if (typeof fragment === "string") {
    if (!fragment.startsWith("fragment")) {
      fragment = `fragment tmp on ${resourceName} ${fragment}`;
    }

    try {
      parsedFragment = parse(fragment);
    } catch (e) {
      throw new Error(
        `Invalid fragment given for resource '${resourceName}' and fetchType '${fetchType}' (${e.message}).`
      );
    }
  }

  return (parsedFragment as any).definitions?.[0].selectionSet.selections;
};

const filterFields = (
  fields: FieldNode[],
  fragment: WhiteListFragment | BlackListFragment
): FieldNode[] => {
  return fields.filter((f) => {
    const match = fragment.fields.includes(f.name.value);
    if (fragment.type === "whitelist") {
      // only include these
      return match;
    } else {
      // blacklist, exclude these
      return !match;
    }
  });
};

export default (introspectionResults: IntrospectionResult) =>
  (
    resource: Resource,
    aorFetchType: FetchType,
    variables: { [key: string]: any },
    fragment: ResourceFragment
  ) => {
    const queryType = resource[aorFetchType];
    const queryDialect = "typegraphql";

    if (!queryType) {
      throw new Error(
        `No query or mutation matching aor fetch type ${aorFetchType} could be found for resource ${resource.type.name}`
      );
    }

    const { orderBy, skip, take, ...countVariables } = variables;
    const apolloArgs = buildApolloArgs(queryType, variables);
    const args = buildArgs(queryType, variables);
    const countArgs = buildArgs(queryType, countVariables);

    const buildRawFields = () =>
      buildFields(
        introspectionResults,
        resource.type.name
      )((resource.type as any).fields);
    const fields = fragment
      ? isDocumentNodeFragment(fragment) // its a document node
        ? buildFieldsFromFragment(fragment, resource.type.name, aorFetchType)
        : /** blaclist or whitelist**/
          filterFields(buildRawFields(), fragment)
      : buildRawFields();

    const totals = {
      typegraphql: (): FieldNode => {
        const resourceName = upperFirst(resource.type.name);

        const aggregateType = introspectionResults.types.find(
          (n) => n.name === `Aggregate${resourceName}`
        );
        // older versions use "count", newer "_count"
        const countName =
          aggregateType.kind === "OBJECT" &&
          aggregateType.fields.find((f) => f.name === "_count")
            ? "_count"
            : "count";
        return gqlTypes.field(gqlTypes.name(`aggregate${resourceName}`), {
          alias: gqlTypes.name("total"),
          arguments: countArgs,
          selectionSet: gqlTypes.selectionSet([
            gqlTypes.field(gqlTypes.name(countName), {
              selectionSet: gqlTypes.selectionSet([
                gqlTypes.field(gqlTypes.name("_all"))
              ])
            })
          ])
        });
      }
    };

    if (
      aorFetchType === GET_LIST ||
      aorFetchType === GET_MANY ||
      aorFetchType === GET_MANY_REFERENCE
    ) {
      return gqlTypes.document([
        gqlTypes.operationDefinition(
          // @ts-ignore
          "query",
          gqlTypes.selectionSet([
            gqlTypes.field(gqlTypes.name(queryType.name!), {
              alias: gqlTypes.name("items"),
              arguments: args,
              selectionSet: gqlTypes.selectionSet(fields)
            }),
            totals[queryDialect]()
          ]),
          gqlTypes.name(queryType.name!),
          apolloArgs
        )
      ]);
    }

    if (aorFetchType === DELETE) {
      return gqlTypes.document([
        gqlTypes.operationDefinition(
          // @ts-ignore
          "mutation",
          gqlTypes.selectionSet([
            gqlTypes.field(gqlTypes.name(queryType.name!), {
              alias: gqlTypes.name("data"),
              arguments: args,
              selectionSet: gqlTypes.selectionSet([
                gqlTypes.field(gqlTypes.name("id"))
              ])
            })
          ]),
          gqlTypes.name(queryType.name!),
          apolloArgs
        )
      ]);
    }

    return gqlTypes.document([
      gqlTypes.operationDefinition(
        // @ts-ignore
        QUERY_TYPES.includes(aorFetchType) ? "query" : "mutation",
        gqlTypes.selectionSet([
          gqlTypes.field(gqlTypes.name(queryType.name!), {
            alias: gqlTypes.name("data"),
            arguments: args,
            selectionSet: gqlTypes.selectionSet(fields)
          })
        ]),
        gqlTypes.name(queryType.name!),
        apolloArgs
      )
    ]);
  };
